import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from './ui/card';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine
} from 'recharts';
import { TrendingUp, TrendingDown, Minus, AlertCircle } from 'lucide-react';
import { useDataChangeListener } from '../utils/dataBus';
import PulseValue from './PulseValue';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper local (pas de mélange avec formatDuration)
const formatFCFA = (n) => {
  const v = parseInt(n, 10) || 0;
  return v.toLocaleString('fr-FR');
};

const WEEKDAY_LABELS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

// Format compact : 14000 → "14k", 8500 → "8,5k"
const formatK = (n) => {
  const v = Math.round(n);
  if (v < 1000) return `${v}`;
  const k = v / 1000;
  return k >= 10 ? `${Math.round(k)}k` : `${k.toFixed(1).replace('.', ',').replace(',0', '')}k`;
};

const StatutBadge = ({ statut }) => {
  const cfg = {
    avance: { label: 'En avance', color: 'text-[#10B981] bg-[#10B981]/15 border-[#10B981]/40', Icon: TrendingUp },
    retard: { label: 'En retard', color: 'text-[#EF4444] bg-[#EF4444]/15 border-[#EF4444]/40', Icon: TrendingDown },
    aligne: { label: 'Aligné', color: 'text-[#C6A15B] bg-[#C6A15B]/15 border-[#C6A15B]/40', Icon: Minus },
    non_defini: { label: 'Objectif non défini', color: 'text-[#EAEAEA]/60 bg-[#EAEAEA]/10 border-[#EAEAEA]/20', Icon: AlertCircle },
  };
  const c = cfg[statut] || cfg.non_defini;
  const Icon = c.Icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-bold uppercase tracking-wide ${c.color}`}>
      <Icon className="w-3.5 h-3.5" />
      {c.label}
    </span>
  );
};

const MiniKpi = ({ label, value, rawValue, suffix = 'FCFA', tone = 'default', testId, pulse = false }) => {
  const toneClass = {
    default: 'text-[#EAEAEA]',
    gold: 'text-[#FFD700]',
    green: 'text-[#10B981]',
    red: 'text-[#EF4444]',
    muted: 'text-[#EAEAEA]/50',
  }[tone];
  const valueNode = (
    <>
      {value} {suffix && <span className="text-xs font-medium opacity-70">{suffix}</span>}
    </>
  );
  return (
    <div className="flex-1 min-w-[130px] rounded-xl bg-[#0B132B]/60 border border-[#C6A15B]/20 px-3 py-2.5" data-testid={testId}>
      <div className="text-[10px] text-[#EAEAEA]/60 uppercase tracking-wider">{label}</div>
      <div className={`text-lg font-bold mt-0.5 ${toneClass}`}>
        {pulse ? <PulseValue value={rawValue}>{valueNode}</PulseValue> : valueNode}
      </div>
    </div>
  );
};

const CommercialCard = ({ c, moisNom, showPrevisionnel }) => {
  const { objectifJournalier, caAujourdhui, soldeCumule, objectifRecommande, aRattraper, statut, todayIsSunday, rattrapageDimanche, dailySeries, nom, poste } = c;
  const objDefini = objectifJournalier > 0;

  // CEO Session 8 quinquies — Règle cumul finale :
  // - Objectif du jour = 0 le dimanche, sinon = objectifJournalier
  // - cumul_après_jour = cumul_veille + CA_réel - objectif_du_jour
  // - Barre de base = CA réel (NEUTRE beige lun-sam, BLEU dimanche)
  // - Si cumul_après < 0 : stack ROUGE = |cumul_après| empilé sur la base (peut dépasser la ligne)
  // - Si cumul_après > 0 : stack VERT  =  cumul_après  empilé sur la base (au-dessus de la ligne)
  // - Labels affichent le CUMUL (pas le delta du jour) : c'est ce qui reste à rattraper / ce qui est en avance
  let runningSolde = 0;
  const chartData = dailySeries.map(d => {
    const balanceBeforeDay = runningSolde;
    const objectifDuJour = (!objDefini || d.isSunday) ? 0 : objectifJournalier;
    // Objectif ajusté (pour tooltip uniquement)
    let objectifAjuste;
    if (!objDefini) objectifAjuste = 0;
    else if (d.isSunday) objectifAjuste = Math.max(0, -balanceBeforeDay);
    else objectifAjuste = Math.max(0, objectifJournalier - balanceBeforeDay);

    let caBase = 0, manque = 0, surplus = 0, aligne = false;
    let soldeApres = balanceBeforeDay;
    let retardBonusJour = 0; // delta du jour seul, pour ligne 6 du tooltip

    if (d.isPastOrToday) {
      retardBonusJour = d.caReel - objectifDuJour;
      soldeApres = balanceBeforeDay + retardBonusJour;
      runningSolde = soldeApres;

      caBase = d.caReel;
      if (objDefini) {
        if (soldeApres < 0) manque = -soldeApres;
        else if (soldeApres > 0) surplus = soldeApres;
        else aligne = true;
      }
    }

    return {
      day: d.day,
      dayLabel: `${d.day}`,
      weekday: d.weekday,
      caReel: d.isPastOrToday ? d.caReel : 0,
      objectifDuJour,
      objectifAjuste,
      balanceBeforeDay,
      soldeApres: d.isPastOrToday ? soldeApres : null,
      retardBonusJour: d.isPastOrToday ? retardBonusJour : 0,
      caBase,
      manque,
      surplus,
      aligne,
      caPrevisionnel: 0,
      isSunday: d.isSunday,
      isToday: d.isToday,
      isPastOrToday: d.isPastOrToday,
      isFuture: !d.isPastOrToday,
    };
  });

  // --- Prévisionnel de rattrapage (CEO Session 8 sexies) ---
  // Affiché UNIQUEMENT si showPrevisionnel === true (toggle admin).
  // Pour les jours futurs jusqu'au prochain dimanche inclus :
  // - Si cumul_aujourd'hui < 0 : afficher en pointillé discret le CA minimum
  //   pour effacer le retard d'ici dimanche (répartition uniforme).
  // - Chaque jour futur ouvré (lun-sam) : CA_min = objectifJournalier + part_rattrapage
  // - Dimanche futur : CA_min = part_rattrapage (pas d'objectif fixe le dim)
  // - Si cumul >= 0 : pas de prévisionnel affiché.
  if (objDefini && showPrevisionnel) {
    const todayIdx = chartData.findIndex(d => d.isToday);
    if (todayIdx >= 0) {
      const cumulToday = chartData[todayIdx].soldeApres || 0;
      if (cumulToday < 0) {
        let nextSundayIdx = -1;
        for (let i = todayIdx + 1; i < chartData.length; i++) {
          if (chartData[i].isSunday) { nextSundayIdx = i; break; }
        }
        if (nextSundayIdx > todayIdx) {
          const nbFuturs = nextSundayIdx - todayIdx; // jours de demain à dim. inclus
          const partRattrapage = Math.abs(cumulToday) / nbFuturs;
          for (let i = todayIdx + 1; i <= nextSundayIdx; i++) {
            chartData[i].caPrevisionnel = chartData[i].isSunday
              ? Math.round(partRattrapage)
              : Math.round(objectifJournalier + partRattrapage);
          }
        }
      }
    }
  }

  // Tooltip enrichi (CEO)
  const renderTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const e = payload[0].payload;
    const weekLabels = ['lun','mar','mer','jeu','ven','sam','dim'];
    const jourStr = `${String(e.day).padStart(2, '0')} (${weekLabels[e.weekday] || ''})`;
    if (e.isFuture) {
      return (
        <div className="bg-[#0B132B] border border-[#C6A15B]/60 rounded-lg p-3 text-xs text-[#EAEAEA]">
          <div className="font-bold text-[#FFD700] mb-1">Jour {jourStr}</div>
          <div className="text-[#EAEAEA]/60 italic">Jour futur — pas encore de données</div>
        </div>
      );
    }
    const soldeColor = e.soldeApres > 0 ? '#10B981' : e.soldeApres < 0 ? '#EF4444' : '#C6A15B';
    const balanceColor = e.balanceBeforeDay > 0 ? '#10B981' : e.balanceBeforeDay < 0 ? '#EF4444' : '#C6A15B';
    const rbjColor = e.retardBonusJour > 0 ? '#10B981' : e.retardBonusJour < 0 ? '#EF4444' : '#C6A15B';
    return (
      <div className="bg-[#0B132B] border border-[#C6A15B]/60 rounded-lg p-3 text-xs text-[#EAEAEA] min-w-[260px]">
        <div className="font-bold text-[#FFD700] mb-2 border-b border-[#C6A15B]/30 pb-1">
          Jour {jourStr}{e.isSunday ? ' — rattrapage' : ''}
        </div>
        <div className="space-y-1">
          <div className="flex justify-between"><span className="text-[#EAEAEA]/70">CA réel :</span><strong>{formatFCFA(e.caReel)} FCFA</strong></div>
          <div className="flex justify-between"><span className="text-[#EAEAEA]/70">Objectif journalier :</span><span>{e.isSunday ? '— (dim.)' : `${formatFCFA(objectifJournalier)} FCFA`}</span></div>
          <div className="flex justify-between border-t border-[#C6A15B]/20 pt-1 mt-1">
            <span className="text-[#EAEAEA]/70">Solde reporté avant :</span>
            <span style={{ color: balanceColor }}>
              {e.balanceBeforeDay > 0 ? '+' : ''}{formatFCFA(e.balanceBeforeDay)} FCFA
            </span>
          </div>
          <div className="flex justify-between"><span className="text-[#EAEAEA]/70">Objectif du jour après report :</span><strong className="text-[#FFD700]">{formatFCFA(e.objectifAjuste)} FCFA</strong></div>
          <div className="flex justify-between">
            <span className="text-[#EAEAEA]/70">{e.retardBonusJour >= 0 ? 'Bonus du jour :' : 'Retard du jour :'}</span>
            <strong style={{ color: rbjColor }}>
              {e.retardBonusJour > 0 ? '+' : ''}{formatFCFA(e.retardBonusJour)} FCFA
            </strong>
          </div>
          <div className="flex justify-between border-t border-[#C6A15B]/20 pt-1 mt-1">
            <span className="text-[#EAEAEA]/70">Solde cumulé après :</span>
            <strong style={{ color: soldeColor }}>
              {e.soldeApres > 0 ? '+' : ''}{formatFCFA(e.soldeApres)} FCFA
            </strong>
          </div>
          {e.aligne && <div className="text-center text-[#10B981] font-bold mt-1">✓ Aligné sur l'objectif</div>}
        </div>
      </div>
    );
  };

  return (
    <Card className="bg-gradient-to-br from-[#1C2541]/80 to-[#2A3B5C]/60 border-2 border-[#C6A15B]/25 backdrop-blur-xl overflow-hidden">
      <CardContent className="p-3 sm:p-5 space-y-3 sm:space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-[#FFD700] font-bold text-base sm:text-lg truncate">{nom}</h3>
            {poste && <p className="text-[#EAEAEA]/60 text-xs truncate">{poste}</p>}
          </div>
          <StatutBadge statut={statut} />
        </div>

        {/* Mini-KPI (inchangés) */}
        <div className="flex flex-wrap gap-2">
          <MiniKpi label="Objectif/jour" value={objDefini ? formatFCFA(objectifJournalier) : '—'} suffix={objDefini ? 'FCFA' : ''} tone={objDefini ? 'gold' : 'muted'} testId={`kpi-obj-${nom}`} />
          <MiniKpi label="CA aujourd'hui" value={formatFCFA(caAujourdhui)} rawValue={caAujourdhui} pulse tone="default" testId={`kpi-ca-today-${nom}`} />
          <MiniKpi label={soldeCumule >= 0 ? 'Avance cumulée' : 'Retard cumulé'} value={formatFCFA(Math.abs(soldeCumule))} rawValue={soldeCumule} pulse tone={!objDefini ? 'muted' : soldeCumule > 0 ? 'green' : soldeCumule < 0 ? 'red' : 'default'} testId={`kpi-solde-${nom}`} />
          <MiniKpi label={todayIsSunday ? 'Rattrapage dim.' : 'Obj. recommandé'} value={objDefini ? formatFCFA(objectifRecommande) : '—'} suffix={objDefini ? 'FCFA' : ''} tone={objDefini && objectifRecommande > objectifJournalier ? 'red' : 'gold'} testId={`kpi-recommande-${nom}`} />
          <MiniKpi label="À rattraper" value={formatFCFA(aRattraper)} rawValue={aRattraper} pulse tone={aRattraper > 0 ? 'red' : 'muted'} testId={`kpi-rattraper-${nom}`} />
        </div>

        {!objDefini && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#EAEAEA]/5 border border-[#EAEAEA]/15 text-[#EAEAEA]/70 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>Objectif journalier non défini. L'admin peut le fixer depuis Admin → Commerciaux.</span>
          </div>
        )}

        {/* Bandeau Objectif (style chip premium, hors zone de données) */}
        {objDefini && (
          <div className="flex items-center justify-between gap-3 pt-1" data-testid={`objectif-row-${nom}`}>
            <span className="text-[10px] text-[#EAEAEA]/50 uppercase tracking-[0.15em] font-semibold">
              Performance du mois
            </span>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#FFD700]/10 border border-[#FFD700]/35 text-[#FFD700] text-[11px] font-bold whitespace-nowrap" data-testid={`objectif-badge-${nom}`}>
              <span className="w-3 h-0.5 bg-[#FFD700]" />
              Objectif : {formatFCFA(objectifJournalier)} FCFA/jour
            </span>
          </div>
        )}

        {/* Graphique CEO-compliant : stacked bars + ligne horizontale objectif, pas de courbe jaune ni axe Y droit */}
        <div className="relative">
          <div className="w-full overflow-x-auto">
          <div className="h-[380px]" style={{ minWidth: `${Math.max(600, chartData.length * 30)}px` }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 32, right: 20, left: 5, bottom: 28 }}>
                <CartesianGrid stroke="#C6A15B20" vertical={false} />
                <XAxis
                  dataKey="dayLabel"
                  interval={0}
                  minTickGap={0}
                  axisLine={{ stroke: '#C6A15B40' }}
                  height={36}
                  tick={(props) => {
                    const { x, y, payload } = props;
                    const dayNum = parseInt(payload.value, 10);
                    const entry = chartData.find(d => d.day === dayNum);
                    const isSun = entry?.isSunday;
                    const isToday = entry?.isToday;
                    return (
                      <g>
                        {isToday && <rect x={x - 10} y={y + 4} width={20} height={14} rx={3} fill="#FFD70020" stroke="#FFD70080" />}
                        <text
                          x={x}
                          y={y + 14}
                          textAnchor="middle"
                          fill={isSun ? '#3B82F6' : isToday ? '#FFD700' : '#EAEAEA90'}
                          fontSize={isSun || isToday ? 11 : 10}
                          fontWeight={isSun || isToday ? 700 : 500}
                        >
                          {payload.value}
                        </text>
                      </g>
                    );
                  }}
                />
                <YAxis
                  tick={{ fill: '#EAEAEA90', fontSize: 10 }}
                  axisLine={{ stroke: '#C6A15B40' }}
                  tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}
                />
                <Tooltip content={renderTooltip} cursor={{ fill: '#FFD70010' }} />

                {/* LIGNE HORIZONTALE FIXE = Objectif journalier (toujours visible) */}
                {objDefini && (
                  <ReferenceLine
                    y={objectifJournalier}
                    stroke="#FFD700"
                    strokeDasharray="6 4"
                    strokeWidth={2}
                  />
                )}

                {/* STACK 1 : caBase = CA réel (base)
                    - Couleur Lun-Sam = NEUTRE beige/doré (#C6A15B)
                    - Couleur Dimanche = BLEU (#3B82F6)
                    - Label interne "Xk" (CA réel) en petit, discret
                */}
                <Bar dataKey="caBase" stackId="ca" radius={[0, 0, 0, 0]} isAnimationActive={false}
                  shape={(props) => {
                    const { x, y, width, height, payload } = props;
                    if (!height || height <= 0) return null;
                    let color;
                    if (payload.isSunday) color = '#3B82F6';
                    else color = '#C6A15B';
                    const opacity = payload.isToday ? 1 : 0.9;
                    const showK = payload.isPastOrToday && payload.caReel > 0 && height >= 16 && width >= 16;
                    const innerColor = payload.isSunday ? '#FFFFFF' : '#0B132B';
                    return (
                      <g>
                        <rect x={x} y={y} width={width} height={height}
                          fill={color} fillOpacity={opacity}
                          stroke={payload.isToday ? '#FFD700' : 'none'}
                          strokeWidth={payload.isToday ? 2 : 0} />
                        {showK && (
                          <text x={x + width / 2} y={y + height / 2 + 3} textAnchor="middle"
                            fill={innerColor} fillOpacity={0.7}
                            fontSize={9} fontWeight={600} letterSpacing={0.2}>
                            {formatK(payload.caReel)}
                          </text>
                        )}
                      </g>
                    );
                  }} />

                {/* STACK 2 : manque (ROUGE, cumul négatif empilé sur caBase) */}
                <Bar dataKey="manque" stackId="ca" radius={[3, 3, 0, 0]} isAnimationActive={false} shape={(props) => {
                  const { x, y, width, height, payload } = props;
                  if (!height || height <= 0) return null;
                  return (
                    <g>
                      <rect x={x} y={y} width={width} height={height} rx={3} ry={3}
                        fill="#EF4444" fillOpacity={payload.isToday ? 0.85 : 0.75} />
                      {payload.isToday ? (
                        <text x={x + width / 2} y={y - 4} textAnchor="middle"
                          fill="#EF4444" fillOpacity={0.9}
                          fontSize={9} fontWeight={500} letterSpacing={0.3}>
                          {`−${formatFCFA(payload.manque)} F`}
                        </text>
                      ) : (height >= 18 && (
                        <text x={x + width / 2} y={y + height / 2 + 3} textAnchor="middle"
                          fill="#FFFFFF" fillOpacity={0.92}
                          fontSize={9} fontWeight={700} letterSpacing={0.2}>
                          {`−${formatK(payload.manque)}`}
                        </text>
                      ))}
                    </g>
                  );
                }} />

                {/* STACK 3 : surplus (VERT, cumul positif empilé au-dessus de la ligne) */}
                <Bar dataKey="surplus" stackId="ca" radius={[3, 3, 0, 0]} isAnimationActive={false} shape={(props) => {
                  const { x, y, width, height, payload } = props;
                  if (!height || height <= 0) return null;
                  return (
                    <g>
                      <rect x={x} y={y} width={width} height={height} rx={3} ry={3}
                        fill="#10B981" fillOpacity={payload.isToday ? 1 : 0.9}
                        stroke={payload.isToday ? '#FFD700' : 'none'}
                        strokeWidth={payload.isToday ? 2 : 0} />
                      {payload.isToday ? (
                        <text x={x + width / 2} y={y - 4} textAnchor="middle"
                          fill="#10B981" fillOpacity={0.9}
                          fontSize={9} fontWeight={500} letterSpacing={0.3}>
                          {`+${formatFCFA(payload.surplus)} F`}
                        </text>
                      ) : (height >= 18 && (
                        <text x={x + width / 2} y={y + height / 2 + 3} textAnchor="middle"
                          fill="#FFFFFF" fillOpacity={0.95}
                          fontSize={9} fontWeight={700} letterSpacing={0.2}>
                          {`+${formatK(payload.surplus)}`}
                        </text>
                      ))}
                    </g>
                  );
                }} />

                {/* STACK 4 : caPrevisionnel (POINTILLÉ léger, discret, jours futurs rattrapage) */}
                <Bar dataKey="caPrevisionnel" stackId="ca" isAnimationActive={false} shape={(props) => {
                  const { x, y, width, height, payload } = props;
                  if (!height || height <= 0 || !payload.isFuture || !payload.caPrevisionnel) return null;
                  return (
                    <g>
                      <rect x={x + 1} y={y} width={Math.max(0, width - 2)} height={height} rx={3} ry={3}
                        fill="#C6A15B" fillOpacity={0.06}
                        stroke="#C6A15B" strokeOpacity={0.55}
                        strokeDasharray="4 3" strokeWidth={1.2} />
                      <text x={x + width / 2} y={y - 4} textAnchor="middle"
                        fill="#C6A15B" fillOpacity={0.75}
                        fontSize={8} fontWeight={500} letterSpacing={0.3}>
                        {formatK(payload.caPrevisionnel)}
                      </text>
                    </g>
                  );
                }} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        </div>

        {/* Rattrapage dimanche si retard */}
        {objDefini && rattrapageDimanche > 0 && !todayIsSunday && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#3B82F6]/10 border border-[#3B82F6]/30 text-[#93C5FD] text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>Rattrapage recommandé dimanche : <strong>{formatFCFA(rattrapageDimanche)} FCFA</strong></span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const SuiviCaJournalier = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPrevisionnel, setShowPrevisionnel] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/dashboard/suivi-ca-journalier`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      console.error('Erreur fetch suivi CA journalier', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // refresh toutes les 30s pour suivre l'évolution du CA du jour
    const itv = setInterval(fetchData, 30000);
    return () => clearInterval(itv);
  }, [fetchData]);

  // Refresh instantané quand une transaction/dépense/dépôt est créé/modifié/supprimé
  useDataChangeListener(fetchData);

  if (loading) {
    return (
      <div className="rounded-3xl bg-gradient-to-br from-[#1C2541]/90 to-[#2A3B5C]/90 backdrop-blur-xl p-8 border-2 border-[#C6A15B]/30 mb-6">
        <div className="text-[#C6A15B] text-center">Chargement du suivi journalier...</div>
      </div>
    );
  }

  // Staff sans fiche mappée
  if (data?.no_commercial_mapped) {
    return (
      <div
        data-testid="suivi-journalier-no-mapped"
        className="rounded-3xl bg-gradient-to-br from-[#1C2541]/90 to-[#2A3B5C]/90 backdrop-blur-xl p-8 border-2 border-[#F59E0B]/40 mb-6"
      >
        <div className="text-center py-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#F59E0B]/20 border border-[#F59E0B]/40 mb-4">
            <AlertCircle className="w-8 h-8 text-[#F59E0B]" />
          </div>
          <h3 className="text-[#F59E0B] text-xl font-bold mb-2">Suivi CA Journalier</h3>
          <p className="text-[#EAEAEA]/80 text-base">
            Aucun objectif journalier lié à votre compte. Contactez l'administrateur.
          </p>
        </div>
      </div>
    );
  }

  if (!data || !data.commerciaux || data.commerciaux.length === 0) {
    return null;
  }

  const isAdminOrCashier = user?.role === 'admin' || user?.role === 'cashier';

  return (
    <div className="rounded-2xl sm:rounded-3xl bg-gradient-to-br from-[#1C2541]/90 to-[#2A3B5C]/90 backdrop-blur-xl p-3 sm:p-6 border-2 border-[#C6A15B]/30 mb-6" data-testid="suivi-ca-journalier-block">
      <div className="flex items-start justify-between mb-4 sm:mb-5 gap-3 flex-wrap">
        <div className="min-w-0">
          <h2 className="text-[#FFD700] text-lg sm:text-2xl font-bold leading-tight">Suivi CA Journalier</h2>
          <p className="text-[#EAEAEA]/70 text-xs sm:text-sm">
            {data.moisNom} • {isAdminOrCashier ? `${data.commerciaux.length} commercial${data.commerciaux.length > 1 ? 'aux' : ''}` : 'Votre performance'}
          </p>
        </div>

        <div className="flex items-center gap-4 flex-wrap justify-end">
          {/* Toggle Prévisionnel de rattrapage */}
          <button
            type="button"
            onClick={() => setShowPrevisionnel(v => !v)}
            data-testid="toggle-previsionnel-btn"
            className={`group inline-flex items-center gap-2 px-3.5 py-2 rounded-full border text-xs font-semibold uppercase tracking-wider transition-all ${
              showPrevisionnel
                ? 'bg-[#C6A15B]/20 border-[#C6A15B] text-[#FFD700] shadow-[0_0_12px_rgba(198,161,91,0.35)]'
                : 'bg-[#0B132B]/50 border-[#C6A15B]/30 text-[#EAEAEA]/70 hover:border-[#C6A15B]/60 hover:text-[#EAEAEA]'
            }`}
            title={showPrevisionnel ? 'Masquer la simulation de rattrapage' : 'Afficher la simulation de rattrapage jusqu\'à dimanche'}
          >
            <span className={`relative inline-block w-8 h-4 rounded-full border transition-colors ${
              showPrevisionnel ? 'bg-[#C6A15B] border-[#C6A15B]' : 'bg-[#1C2541] border-[#C6A15B]/40'
            }`}>
              <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-[#0B132B] transition-all ${
                showPrevisionnel ? 'left-[17px]' : 'left-0.5'
              }`} />
            </span>
            Simulation rattrapage
          </button>

          <div className="hidden md:flex items-center gap-4 text-xs text-[#EAEAEA]/60 flex-wrap justify-end">
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#C6A15B]" /> CA réel (lun-sam)</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#3B82F6]" /> CA réel dimanche</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#EF4444]" /> Retard cumulé</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#10B981]" /> Avance cumulée</span>
            {showPrevisionnel && (
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm border border-dashed border-[#C6A15B]" /> Rattrapage prévisionnel</span>
            )}
            <span className="flex items-center gap-1.5"><span className="w-5 h-0.5 bg-[#FFD700]" /> Objectif journalier</span>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-5">
        {data.commerciaux.map(c => (
          <CommercialCard key={c.id} c={c} moisNom={data.moisNom} showPrevisionnel={showPrevisionnel} />
        ))}
      </div>
    </div>
  );
};

export default SuiviCaJournalier;
